import { useCallback } from 'react';
import { Flex, RemoteImage, Typography } from '@banhala/ui-kit';
import type {
  TwoColFeaturedShortcutListProps,
  FeaturedShortcutEntity,
} from './types';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

export const TwoColFeaturedShortcutList = ({
  entity,
  logging,
}: TwoColFeaturedShortcutListProps) => {
  if (!entity || entity.length === 0) return null;

  // entity가 2중 배열 또는 flat 배열일 수 있음
  const rows: FeaturedShortcutEntity[][] = Array.isArray(entity[0])
    ? (entity as FeaturedShortcutEntity[][])
    : pairItems(entity as FeaturedShortcutEntity[]);

  return (
    <Flex padding={{ top: 4, horizontal: 16, bottom: 8 }} gap={12}>
      {rows.map((row, rowIndex) => (
        <Flex.Row key={rowIndex} gap={12}>
          {row.map(item => (
            <ShortcutCard
              key={item.item.deeplink}
              entity={item}
              logging={logging}
            />
          ))}
          {row.length === 1 && <Flex flex={1} />}
        </Flex.Row>
      ))}
    </Flex>
  );
};

const ShortcutCard = ({
  entity,
  logging,
}: {
  entity: FeaturedShortcutEntity;
  logging: IScreensLogging;
}) => {
  const { item } = entity;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePress = useCallback(() => {
    logAnalyticsEvent({
      name: 'CLICK_TWO_COL_FEATURED_SHORTCUT_LIST',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, entity.logging, openDeeplink, logAnalyticsEvent]);

  return (
    <Flex
      flex={1}
      borderRadius={10}
      backgroundColor="white"
      onPress={handlePress}
      shadowStep="large"
    >
      <Flex.Row alignItems="center" padding={{ horizontal: 12, vertical: 10 }}>
        <Flex
          width={56}
          height={56}
          borderRadius={16}
          overflow="hidden"
          margin={{ right: 12 }}
        >
          <RemoteImage
            uri={item.representative_icon}
            width={56}
            height={56}
            resizeMode="cover"
          />
        </Flex>
        <Flex flex={1} gap={2}>
          <Typography type="subtitle3" color="content_primary" numberOfLines={1}>
            {item.name}
          </Typography>
          <Typography type="body5" color="content_secondary" numberOfLines={1}>
            {item.description}
          </Typography>
        </Flex>
      </Flex.Row>

      {/* Circle images (overlapping) */}
      {item.circle_images != null && item.circle_images.length > 0 && (
        <Flex.Row padding={{ horizontal: 12, bottom: 10 }}>
          {item.circle_images.map((img, index) => (
            <Flex
              key={img}
              width={43}
              height={43}
              borderRadius={21}
              overflow="hidden"
              borderWidth={1}
              borderColorValue="rgba(0,0,0,0.08)"
              margin={{ left: index > 0 ? -4 : 0 }}
            >
              <RemoteImage uri={img} width={43} height={43} resizeMode="cover" />
            </Flex>
          ))}
        </Flex.Row>
      )}
    </Flex>
  );
};

const pairItems = <T,>(items: T[]): T[][] => {
  const pairs: T[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    pairs.push(items.slice(i, i + 2));
  }
  return pairs;
};
